import {Inline, Stack, Text, useViewport, useWindowHeight} from '@treatwell/ui';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';
import {useAppSelector} from '../../hooks/store';
import {
  selectShowMultiStaffSelection,
  selectSelectedTotalPrice,
} from '../../store/slices/selectors';
import styles from './OrderTotal.module.css';
import {STICKY_FOOTER_VIEWPORT_HEIGHT} from '../App/App';
import {useSelector} from 'react-redux';

interface Props {
  label: string;
  align?: 'start' | 'end';
  type?: 'body' | 'bodyHeavy' | 'mdHeader' | 'lgHeader';
  total?: string;
  shouldShowMultiStaffSelection?: boolean;
  isDesktop: boolean;
  isSticky?: boolean;
}

export const OrderTotal = ({
  label,
  total,
  align = 'end',
  type = 'mdHeader',
  isDesktop,
  isSticky,
}: Props) => {
  return (
    <div
      className={clsx(styles.root, {
        [styles.hideTotal]: !total,
        [styles.centered]: isSticky,
      })}
    >
      {isDesktop ? (
        <Inline
          justify={isSticky ? 'start' : 'between'}
          data-cy="OrderTotal"
          space="md"
        >
          <Text as="dt" type={type} className={styles.text}>
            {label}
          </Text>
          <Text as="dd" type="bodyHeavy" className={styles.text}>
            {total}
          </Text>
        </Inline>
      ) : (
        <Stack as="dl" align={align} data-cy="OrderTotal">
          <Text as="dt" type="bodyHeavy" className={styles.text}>
            {label}
          </Text>
          <Text as="dd" type={type} className={styles.text}>
            {total}
          </Text>
        </Stack>
      )}
    </div>
  );
};

export const OrderTotalContainer = ({
  align,
  type,
}: Pick<Props, 'align' | 'type'>) => {
  const {t} = useTranslation();
  const shouldShowMultiStaffSelection = useAppSelector(
    selectShowMultiStaffSelection,
  );
  const isDesktop = useViewport({device: 'desktop'});
  const windowHeight = useWindowHeight();

  const totalPrice = useSelector(selectSelectedTotalPrice);

  const orderTotalLabel = t('basket.orderTotal');

  if (totalPrice === '') {
    return null;
  }

  return (
    <OrderTotal
      label={orderTotalLabel}
      total={totalPrice}
      align={align}
      type={type}
      shouldShowMultiStaffSelection={shouldShowMultiStaffSelection}
      isDesktop={isDesktop}
      isSticky={
        windowHeight < STICKY_FOOTER_VIEWPORT_HEIGHT &&
        shouldShowMultiStaffSelection
      }
    />
  );
};
